<template>
    <Row>
        <Col span="16">
            <h4>Export preset</h4>
            <p class="tooltip">Control and adjust the video encoding.</p>
        </Col>
        <Col span="8">
            <Select v-model="videoPreset" size="small" data-testid="download-modal__html-video-preset-select" transfer>
                <Option
                    v-for="option in HTML_VIDEO_PRESET_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                    :data-testid="`download-modal__html-video-preset-select-${option.value}`"
                >
                    {{ option.label }}
                </Option>
            </Select>
        </Col>
    </Row>

    <div class="preset-details" data-testid="download-modal__html-video-preset-details">
        <h4>Output file details, based on your selections:</h4>
        <ul>
            <li v-for="line in selectedPresetDescription" :key="line">- {{ line }}</li>
        </ul>
    </div>

    <Row v-if="videoPreset === HTML_VIDEO_PRESET.CUSTOM">
        <Col span="16">
            <h4>Custom ffmpeg params</h4>
            <p class="tooltip">Encoding params passed to ffmpeg. Only a safe subset of flags is allowed.</p>
            <p class="error">{{ customFfmpegParamsError }}</p>
        </Col>
        <Col span="8">
            <Input
                v-model="customFfmpegParams"
                placeholder="-c:v libx264 -crf 18"
                data-testid="download-modal__html-video-custom-ffmpeg-params-input"
            />
        </Col>
    </Row>

    <DownloadOptionWrapper tooltip="You can choose to manually specify the video length">
        <h4>Video runtime</h4>
        <template #input>
            <Input v-model="duration.value" placeholder="0.00" data-testid="download-video-runtime-value">
                <template #append>
                    <span>sec</span>
                </template>
            </Input>
        </template>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper tooltip="Frame rate of the output video">
        <h4>Frame rate</h4>
        <template #input>
            <Select v-model="fps" size="small" data-testid="download-modal__download-video-fps-select">
                <Option
                    v-for="option in FPS_OPTIONS"
                    :key="option"
                    :value="String(option)"
                    :data-testid="`download-modal__download-video-fps-select-${option}`"
                >
                    {{ option }}
                </Option>
            </Select>
        </template>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper tooltip="Capture audio from the template and include it in the output video">
        <h4>Capture Audio</h4>
        <template #input>
            <Switch v-model="captureAudio" data-testid="download-modal__capture-audio-switch" />
        </template>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper tooltip="Container format of the output video file">
        <h4>Encoding format</h4>
        <template #input>
            <Select
                v-model="encodingFormat"
                size="small"
                data-testid="download-modal__html-video-encoding-format-select"
            >
                <Option
                    v-for="option in HTML_VIDEO_ENCODING_FORMAT_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                    :data-testid="`download-modal__html-video-encoding-format-select-${option.value}`"
                >
                    {{ option.label }}
                </Option>
            </Select>
        </template>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        tooltip="Useful when the banner contains a video with the same fps as the output. Note that it will make the video
        generation slower."
    >
        <h4>Optimise Video Frames</h4>
        <template #input>
            <Switch v-model="optimisedVideoFrames" data-testid="download-modal__optimise-video-frames-switch" />
        </template>
    </DownloadOptionWrapper>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { HTML_VIDEO_PRESET, HTML_VIDEO_PRESET_METADATA } from "shared-utils/enums/htmlVideoExport";

import { useDownloadHTMLVideo } from "@/composables/download/useDownloadHTMLVideo";
import DownloadOptionWrapper from "@/components/Download/DownloadOptionWrapper.vue";
import {
    FPS_OPTIONS,
    HTML_VIDEO_ENCODING_FORMAT_OPTIONS,
    HTML_VIDEO_PRESET_OPTIONS
} from "@/components/Download/consts";

const {
    duration,
    fps,
    captureAudio,
    optimisedVideoFrames,
    videoPreset,
    encodingFormat,
    customFfmpegParams,
    customFfmpegParamsError
} = useDownloadHTMLVideo();

const selectedPresetDescription = computed(() => HTML_VIDEO_PRESET_METADATA[videoPreset.value].description);
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

.tooltip {
    font-size: 12.5px;
}

.error {
    color: $error-color;
    font-size: 10px;
    height: 10px;
}

.preset-details {
    margin-bottom: 10px;

    ul {
        list-style: none;
        font-size: 12px;
    }
}
</style>
